import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { SEARCH_TERM_SECTIONS } from "@/lib/search-term-sections";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/foam-roofing";
const NAME = "Spray Foam Roofing";
const TITLE = "Foam Roofing Phoenix: Costs, Lifespan & Free Quote | Durafoam";
const DESCRIPTION =
  "Phoenix foam roofing since 1989. See what a spray foam roof costs, how long it lasts in Arizona heat, and when to recoat vs. replace. Free on-site inspection.";

const faqs = [
  {
    q: "What is spray foam roofing?",
    a: "Spray foam roofing — SPF, for spray polyurethane foam — is a two-part liquid sprayed onto your roof deck that expands into a rigid, closed-cell insulating layer, then gets sealed with a reflective elastomeric coating. The finished roof is one seamless surface with no seams, laps, or fasteners, which is why it's dominated low-slope roofing in Arizona for decades.",
  },
  {
    q: "Are foam roofs any good?",
    a: "For Phoenix's climate, they're the best-performing low-slope system we install — R-6.5+ of insulation per inch, a surface that reflects 80–85% of solar heat, and no seams to leak. The trade-off is maintenance: the protective coating needs a recoat about every 5 years. Keep up with recoats and a foam roof routinely outlasts the alternatives.",
  },
  {
    q: "How long does a foam roof last in Phoenix?",
    a: "A properly installed SPF roof with a fresh coating typically lasts 25+ years in Phoenix. The foam itself is essentially permanent — what wears is the protective elastomeric coating, which we recoat every 5 years in the Valley's UV. Skipping recoats is the single biggest reason a foam roof fails early in Phoenix.",
  },
  {
    q: "Is foam roofing energy efficient?",
    a: "Yes. SPF delivers R-6.5 to R-7 per inch — the highest insulating value of any roofing material. Combined with a reflective white coating, most Phoenix homeowners see a 15–35% drop in cooling costs after switching from tile or shingle to foam.",
  },
  {
    q: "Can foam roofing be installed over my existing roof?",
    a: "In many cases, yes. SPF bonds to most clean, sound substrates including concrete, metal, built-up, and even some existing tile decks once the tile is removed. Avoiding a full tear-off saves money and keeps debris out of the landfill. We inspect the deck first and tell you honestly whether overlay is safe.",
  },
  {
    q: "Does foam roofing leak?",
    a: "A correctly installed and maintained foam roof is one of the most leak-resistant systems available because it has no seams, fasteners, or laps. Most foam roof leaks we're called to repair trace back to a damaged coating, missed recoat, or a penetration (vent, skylight) that wasn't properly sealed.",
  },
  {
    q: "What does foam roofing cost in Phoenix?",
    a: "Phoenix foam roof installations typically run $5.00–$9.00 per square foot for a standard 1\" foam install — the thickness we use on most homes. 1.5\" is used in specific cases (such as plank wood decks), and 2\"+ is available when you want more R-value. Tear-off, if required, is additional. We give written, line-item estimates so you can compare apples to apples with any other bid.",
  },
  {
    q: "How is spray foam roofing applied?",
    a: "Two liquid components meet at the spray gun and expand about thirty times in volume on contact with your roof deck, curing into a rigid closed-cell layer within seconds. We clean and prep the deck first, mask everything that isn't roof, spray the foam in passes (building it up thicker in low spots so water sheds to the drains), then apply a two-coat reflective elastomeric topcoat at a specified mil thickness. A typical Phoenix home takes one to three days.",
  },
  {
    q: "Is spray foam roofing worth it?",
    a: "On a flat or low-slope Phoenix roof, yes — it's the only common system that insulates (R-6.5+ per inch), has no seams to leak, can be re-sloped to fix ponding, and is renewed with a recoat instead of a tear-off. Most homeowners also see a 15–35% drop in cooling costs coming off tile or shingle. It's less compelling on a steep pitch, where tile or shingle is the right call.",
  },
  {
    q: "What are the disadvantages of a foam roof?",
    a: "Three real ones. It needs a coating renewal roughly every 5 years in Phoenix sun — skip that and the roof fails early. It's mixed and sprayed on site, so installation quality varies far more than with factory-made materials; the crew matters. And the surface can be gouged by dropped tools or heavy foot traffic, so punctures need sealing promptly. There's also a temporary chemical smell and overspray risk during install, which proper masking handles.",
  },
  {
    q: "How thick should a foam roof be?",
    a: "One inch is our standard on most Phoenix homes and meets code and warranty requirements. We go to 1.5 inches on specific substrates such as plank wood decks, and 2 inches or more when a homeowner wants additional R-value or when we're building up low areas to correct ponding. More thickness buys insulation, not waterproofing — the coating does the waterproofing.",
  },
];

export const Route = createFileRoute("/foam-roofing")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="foam-roofing"
      extraSections={SEARCH_TERM_SECTIONS["foam-roofing"]}
      eyebrow="Spray Foam Roofing"
      h1="Spray Foam Roofing (SPF) in Arizona"
      subhead="Seamless spray polyurethane foam roofs that cut cooling bills, eliminate leaks, and last decades in Arizona's brutal sun — installed by a family team that's been doing it since 1989."
      heroBullets={[
        "25+ year roof life with recoats",
        "R-6.5+ per inch insulation",
        "Highly reflective coating cuts AC load",
        "Seamless — no laps, no fasteners",
      ]}
      whatIs={{
        heading: "What is a foam roof?",
        body: (
          <>
            <p>
              A foam roof — formally spray polyurethane foam, or SPF — is a two-part liquid that
              expands into a rigid, closed-cell insulating layer once sprayed onto your deck. After
              the foam cures, we coat it with a reflective elastomeric topcoat that protects the
              foam from UV and gives the roof its bright white, energy-saving finish.
            </p>
            <p>
              The result is a single, monolithic roof surface with no seams, no laps, and no
              fasteners — the three places almost every other roof eventually leaks. SPF has been
              the dominant low-slope roofing system in Phoenix for forty years because nothing else
              handles the heat, the monsoon, and the constant thermal cycling as well.
            </p>
            <p>
              Foam is what we install on the majority of Phoenix-area homes we re-roof. It's also
              what we've put on our own families' homes — that's how confident we are in it. If
              you're weighing the numbers, you can{" "}
              <Link
                to="/foam-roof-replacement-cost-phoenix"
                className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
              >
                see foam roof replacement cost ranges for Phoenix
              </Link>{" "}
              before scheduling an inspection. Cost the blocker? <Link to="/financing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Ask us about payment options</Link>.
            </p>

          </>
        ),
      }}
      climate={{
        heading: "Why foam roofing matters for the Phoenix climate",
        body: (
          <>
            <p>
              Phoenix punishes roofs in three specific ways: surface temperatures over 160°F on dark
              shingles in summer, monsoon storms that drop an inch of rain in twenty minutes, and a
              60–80°F daily temperature swing that flexes every joint and fastener on your roof.
              Foam is engineered for exactly those conditions.
            </p>
            <ul>
              <li>
                <strong>Heat:</strong> A white-coated foam roof reflects 80–85% of solar radiation.
                Attic temperatures drop 20–40°F compared to dark tile or shingle — your AC runs
                less, lasts longer, and costs less to operate.
              </li>
              <li>
                <strong>Monsoon:</strong> Because it's seamless and self-flashing around penetrations,
                SPF doesn't have the failure points that wind-driven rain exploits. It also slopes
                water on flat roofs better than any other system because we can build up additional
                foam to correct dead spots and ponding areas.
              </li>
              <li>
                <strong>Thermal movement:</strong> SPF expands and contracts as one continuous
                membrane. No fasteners back out, no seams split, no flashings work loose.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our foam roof installation process",
        intro:
          "Every Durafoam install follows the same disciplined sequence — no shortcuts, no surprise charges.",
        steps: [
          {
            title: "Free on-site inspection",
            body: "A senior estimator walks your roof, photographs every condition, and gives you a written estimate with foam thickness, coating system, and warranty terms spelled out.",
          },
          {
            title: "Surface prep",
            body: "We pressure-clean the deck, remove failed coatings and loose material, and repair substrate damage. Foam only performs as well as what's underneath it.",
          },
          {
            title: "Spray application",
            body: "Crews spray 1\" of closed-cell foam as standard — built up in passes so water sheds correctly. 1.5\" goes on specific substrates like plank wood decks, and 2\"+ when added R-value is specified. Application is weather-gated — we don't spray if wind or humidity will compromise the bond.",
          },
          {
            title: "Coating and inspection",
            body: "A two-coat elastomeric system goes on at the manufacturer-specified mil thickness, embedded with granules where appropriate. Our owner inspects every job before we collect final payment.",
          },
        ],
      }}
      whyChoose={{
        heading: "Why homeowners pick Durafoam",
        body: (
          <ul>
            <li>Family-owned and locally operated since 1989 — same phone number, same family.</li>
            <li>Arizona license ROC223900 L-42 and BBB accredited since 2008.</li>
            <li>
              <Link to="/rosie-on-the-house" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Rosie on the House Certified Partner since 1996</Link>
              {" "}— nearly 30 years of on-air recommendations for foam roofing in Arizona.
            </li>
            <li>Written estimates with line-item pricing — no verbal handshakes.</li>
            <li>Manufacturer-backed material warranties plus our own workmanship warranty.</li>
            <li>Free recoat reminders so your roof never goes past its service interval.</li>
          </ul>
        ),
      }}
      extraSection={{
        heading: "Where we install foam roofs",
        body: (
          <>
            <p>
              Foam is the roof we've put on more Valley homes than any other system, and the neighborhoods
              tell the story. We spray SPF regularly on{" "}
              <Link to="/paradise-valley-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Paradise Valley custom homes</Link>{" "}
              with parapet-wall flat sections, on{" "}
              <Link to="/scottsdale-roofing-contractor" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">mid-century flat-roof properties in Scottsdale</Link>{" "}
              from Old Town to McCormick Ranch, and on{" "}
              <Link to="/arcadia-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Arcadia ranch homes</Link>{" "}
              where owners are converting failed built-up roofs to foam. We also handle
              {" "}<Link to="/ahwatukee-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">foam recoats and installs in Ahwatukee</Link>{" "}
              and outlying jobs like{" "}
              <Link to="/wickenburg-roofing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Wickenburg and the far West Valley</Link>{" "}
              when the project fits the schedule.
            </p>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Ready for a roof built for Phoenix?"
      ctaBody="Schedule a free inspection and we'll walk you through whether foam makes sense for your home — honestly, even if the answer is no."
    />
  );
}
